import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { BaseWidget } from './BaseWidget';
import usePersonaGoals from '../../../../hooks/personas/usePersonaGoals';
import useEffectNameById from '../../../../hooks/personas/useEffectNameById';
import { goalEffectIds, goalTitle } from '../../../../helpers/goalsHelpers/personaGoalFields';
import { goalStatus, goalStatusLabel } from '../../../../helpers/goalsHelpers/personaGoalStatus';
import ModulePlaceholder from './_shared/ModulePlaceholder';
import templatePersonaNotice from './_shared/templatePersonaNotice';
import NO_PERSONA_TEXT from './_shared/noPersonaText';
import usePublicPersona from './_shared/usePublicPersona';
import { publicGoalsState } from '../../../../helpers/liveReportHelpers/publicPersonaState';

// AI-CONTEXT: See docs/features/livereport-per-deelnemer.md (T7)
// Doelen van de deelnemer van het rapport, met hun status en gekoppelde
// effecten. Het ophalen zit in usePersonaGoals en de weergave in
// PersonaGoalsView, zodat de openbare link (T12) de view kan voeden uit
// `personaData.goals` + `personaData.effects` in plaats van uit Firestore.
// Doelen staan in drie opslagvormen; personaGoalFields leest ze alle drie.

/**
 * @typedef {object} PersonaGoalRow
 * @property {string} key - Doel-ID
 * @property {string} heading - Titel, of de beschrijving als er geen titel is
 * @property {string} description - Beschrijving onder de titel ('' = geen)
 * @property {string} status - 'bereikt' / 'niet-bereikt' / opgeslagen waarde
 * @property {string} statusLabel - Het label van die status
 * @property {Array<string>} effects - Namen van de gekoppelde effecten
 */

/** @param {*} value @returns {string} */
const text = (value) => (typeof value === 'string' ? value.trim() : '');

/**
 * @param {object} goal - Een `PersonaGoals`-doc, in welke vorm ook
 * @param {Object<string, string>} effectNameById
 * @returns {PersonaGoalRow}
 */
const toRow = (goal, effectNameById) => {
    const title = goalTitle(goal, effectNameById);
    const description = text(goal.Description);
    // De oudste vorm heet naar zijn ene effect: dat dan niet nóg eens eronder.
    const titleIsEffect = title !== '' && !text(goal.Title) && !text(goal.Goal);
    const effects = goalEffectIds(goal)
        .map((id) => text(effectNameById[id]))
        .filter((name) => name !== '' && !(titleIsEffect && name === title));

    return {
        key: goal.ID || goal.docid,
        heading: title || description || '(zonder titel)',
        description: title ? description : '',
        status: goalStatus(goal),
        statusLabel: goalStatusLabel(goal),
        effects,
    };
};

/**
 * De weergave van de doelen, zonder ophalen.
 *
 * @param {object} props
 * @param {Array<object>|null} props.goals - De doelen (niet verwijderd, gesorteerd); `null` = nog niet geladen
 * @param {Object<string, string>|null} props.effectNameById - Effectnaam per ID; `null` = nog niet geladen
 * @param {{code?: string, message?: string}|null} props.error - Fout bij het ophalen
 * @returns {JSX.Element}
 */
export const PersonaGoalsView = ({ goals, effectNameById, error }) => {
    if (error) {
        return (
            <p className="persona-goals-widget-message" role="alert">
                De doelen konden niet worden geladen.
            </p>
        );
    }
    // Zolang doelen of effectnamen laden: niets beweren, ook geen '(zonder titel)'.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    if (!Array.isArray(goals) || !effectNameById) {
        return <p className="persona-goals-widget-message">Doelen laden…</p>;
    }
    if (goals.length === 0) {
        return <p className="persona-goals-widget-message">Geen doelen</p>;
    }

    return (
        <ul className="adress-dashboard-list">
            {goals.map((goal) => toRow(goal, effectNameById)).map((row) => (
                <li key={row.key} data-goal={row.key} className="adress-dashboard-list-row">
                    <div className="persona-goals-widget-head">
                        <span className="adress-dashboard-list-title" data-part="heading">{row.heading}</span>
                        <span className={`goals-card-status-pill goals-card-status-pill--${row.status}`} data-part="status">
                            {row.statusLabel}
                        </span>
                    </div>
                    {row.description && (
                        <span className="adress-dashboard-list-meta" data-part="description">{row.description}</span>
                    )}
                    {row.effects.length > 0 && (
                        <ul className="persona-goals-widget-effects">
                            {row.effects.map((name) => (
                                <li key={name} data-part="effect">{name}</li>
                            ))}
                        </ul>
                    )}
                </li>
            ))}
        </ul>
    );
};

/**
 * @extends {BaseWidget}
 */
export class PersonaGoalsWidget extends BaseWidget {

    getName() { return 'Doelen van de deelnemer'; }
    getCategory() { return 'medewerkers'; }
    getRequiredModule() { return 'personas'; }
    getIcon() { return <FlagOutlinedIcon />; }

    getControls() {
        return {};
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * @returns {JSX.Element}
 */
const Renderer = () => {
    const { isPublic, personaData, personasModuleOn, personaId, templateStatus } = usePublicPersona();

    const enabled = personasModuleOn && personaId !== null;
    // Op de link en in een template leest de widget niets: doelen en effectnamen komen van de server.
    const live = usePersonaGoals(personaId, { enabled: enabled && !isPublic });
    const liveEffectNames = useEffectNameById({ enabled: !isPublic });
    const { goals, error, effectNameById } = isPublic
        ? publicGoalsState(personaData)
        : { goals: live.goals, error: live.error, effectNameById: liveEffectNames };

    const notice = templatePersonaNotice(templateStatus);
    if (notice) return notice;
    if (!personasModuleOn) {
        return <ModulePlaceholder mode="disabled" moduleLabel="Deelnemers" />;
    }
    if (personaId === null) {
        return (
            <p className="persona-goals-widget-message">
                {NO_PERSONA_TEXT}
            </p>
        );
    }

    return <PersonaGoalsView goals={goals} effectNameById={effectNameById} error={error} />;
};
